import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { environment } from '@environment';


@Injectable({
  providedIn: 'root'
})
export class MixpanelService {
  private mixpanelToken: string;
  private readonly platformId = inject(PLATFORM_ID);
  private mixpanelPromise: Promise<any | null> | null = null;
  /**
   * Track calls fired before LCP are queued here and flushed once the dynamic
   * `mixpanel-browser` chunk loads. Keeps `chunk-KATTIPCE.js` (mixpanel + its own
   * polyfills) off the critical request chain — it cost ~80 KB and ~500 ms in the
   * QA Lighthouse run before this change.
   */
  private pendingCallbacks: Array<(mp: any) => void> = [];
  private importScheduled = false;

  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /**
   * constructor
   * get mixpanel token and initialize
   */
  constructor() {
    this.mixpanelToken = environment.mixpanelToken;
  }

  /**
   * Initialize mixpanel.
   */
  init(): void {
    /** Schedule the dynamic import without blocking — first track call still flushes through `run`. */
    this.scheduleMixpanelImport();
  }

  /**
   * Triggers the dynamic `import('mixpanel-browser')` either after LCP fires
   * (PerformanceObserver) or on the first user interaction (whichever is sooner),
   * with a hard fallback so analytics still fire even on browsers that never
   * report an LCP entry (background tabs, etc.).
   */
  private scheduleMixpanelImport(): void {
    if (this.importScheduled) {
      return;
    }
    this.importScheduled = true;
    if (!this.isBrowser()) {
      return;
    }

    let triggered = false;
    const loadNow = () => {
      if (triggered) {
        return;
      }
      triggered = true;
      void this.beginMixpanelImport();
    };

    /** PerformanceObserver for LCP — preferred trigger because it implies the critical paint is done. */
    try {
      if (typeof PerformanceObserver !== 'undefined') {
        const lcpObserver = new PerformanceObserver(() => {
          try {
            lcpObserver.disconnect();
          } catch {
            /* ignore */
          }
          this.scheduleIdleCallback(loadNow);
        });
        lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      }
    } catch {
      /* ignore — fallback timers below still cover this case */
    }

    /** Hard backstop: 4 s after `load`, or 6 s after script eval if `load` never fires. */
    const onLoad = () => {
      window.removeEventListener('load', onLoad);
      this.scheduleIdleCallback(loadNow, 4000);
    };
    if (typeof document !== 'undefined' && document.readyState === 'complete') {
      this.scheduleIdleCallback(loadNow, 4000);
    } else {
      window.addEventListener('load', onLoad);
    }
    setTimeout(loadNow, 6000);

    /** First user gesture is usually after LCP and is a strong signal we owe Mixpanel an event. */
    const onInteraction = () => {
      window.removeEventListener('pointerdown', onInteraction, true);
      window.removeEventListener('keydown', onInteraction, true);
      loadNow();
    };
    window.addEventListener('pointerdown', onInteraction, { capture: true, once: true, passive: true });
    window.addEventListener('keydown', onInteraction, { capture: true, once: true, passive: true });
  }

  private scheduleIdleCallback(fn: () => void, delayMs: number = 0): void {
    if (typeof window === 'undefined') {
      return;
    }
    const ric = (window as any).requestIdleCallback as
      | ((cb: () => void, opts?: { timeout?: number }) => number)
      | undefined;
    if (typeof ric === 'function') {
      ric(fn, { timeout: 5000 });
    } else {
      setTimeout(fn, delayMs);
    }
  }

  private beginMixpanelImport(): Promise<any | null> {
    if (this.mixpanelPromise) {
      return this.mixpanelPromise;
    }
    this.mixpanelPromise = import('mixpanel-browser')
      .then((mod: any) => {
        const mp = mod?.default?.default || mod?.default || mod || (window as any)?.mixpanel;
        if (!mp || typeof mp.init !== 'function') {
          this.mixpanelPromise = null;
          return null;
        }
        mp.init(this.mixpanelToken, { persistence: 'localStorage' });
        return mp;
      })
      .catch(() => {
        this.mixpanelPromise = null;
        return null;
      })
      .then((mp) => {
        const queued = this.pendingCallbacks;
        this.pendingCallbacks = [];
        if (mp) {
          for (const cb of queued) {
            try {
              cb(mp);
            } catch {
              /* ignore individual callback failures */
            }
          }
        }
        return mp;
      });
    return this.mixpanelPromise;
  }

  private getMixpanel(): Promise<any | null> {
    if (!this.isBrowser()) {
      return Promise.resolve(null);
    }
    if (this.mixpanelPromise) {
      return this.mixpanelPromise;
    }
    /**
     * Don't pull in the chunk yet — it'll load via {@link scheduleMixpanelImport}.
     * Returning `null` here causes `run()` to queue the callback for post-LCP flush.
     */
    return Promise.resolve(null);
  }

  /** Ensures every Mixpanel event includes a fixed Site Type (overrides same key in payloads). */
  private withSiteType(properties: any = {}): any {
    return { ...properties, 'Site Type': 'Stores' };
  }

  private run(cb: (mixpanelInstance: any) => void): void {
    if (!this.isBrowser()) {
      return;
    }
    /** Lazy-init: if `init()` was never called, schedule the import on first track call. */
    this.scheduleMixpanelImport();
    if (this.mixpanelPromise) {
      void this.mixpanelPromise.then((mp) => {
        if (mp) {
          cb(mp);
        }
      });
      return;
    }
    /** Buffer until {@link scheduleMixpanelImport} kicks off the dynamic import. */
    this.pendingCallbacks.push(cb);
  }


  /**
   * Create new Alias for user
   * Call this method only once in user lifecycle
   *
   * @param {string} alias
   */
  createAlias(alias: any) {
    this.run((mp) => mp.alias(alias, mp.get_distinct_id()));
  }

  /** Identifies the user using the unique id generated by mixpanel for every user */
  identify(userEmail: string) {
    this.run((mp) => mp.identify(userEmail));
  }

  /**
   * 
   * @param properties 
   */
  register(properties: any = {}): void {
    this.run((mp) => mp.register(properties));
  }

  /**
   * Push new action to mixpanel.
   *
   * @param {string} id Name of the action to track.
   * @param {*} [action={}] Actions object with custom properties.
   * @memberof MixpanelService
   */
  track(id: string, action: any = {}): void {
    this.run((mp) => mp.track(id, this.withSiteType(action)));
  } 

  /**
   * setup mixpannel
   *
   */
  setup() {
    this.setSuperProperties({ Platform: 'web' });
  }

 /**
   * setPeople
   * Store user profiles in Mixpanel's People Analytics product
   * @param {Object} properties
   */
  setPeople(properties: any = {}): void {
    this.run((mp) => mp.people.set(properties));
  }

  /**
   * appendPeople
   * Store user profiles in Mixpanel's People Analytics product
   * @param {Object} properties
  */
  appendPeople(properties: any = {}): void {
    this.run((mp) => mp.people.append(properties));
  }

  /**
   * 
   * @param property a property that needs to be incremented
   * The default increment value is 1.
   */
  incrementPeopleProperty(property: string) {
    this.run((mp) => mp.people.increment(property));
  }

  /**
   * setSuperProperties
   *
   * @param {object} properties
   */
  setSuperProperties(properties) {
    this.run((mp) => mp.register(properties));
  }

  /**
   * sendEvent
   *
   * @param {string} event
   * @param {object} properties
   */
  sendEvent(event: any, properties?) {
    if (properties) {
      this.run((mp) => mp.track(event, this.withSiteType(properties)));
    } else {
      this.trackEvent(event);
    }
  }

  /**
   * trackEvent
   * @param {String} event
   */
  trackEvent(event: any) {
    this.run((mp) => mp.track(event, this.withSiteType()));
  }

  /**
   * Reset Mixpanel
   */
  logout() {
    this.run((mp) => mp.reset());
  }

  /**
   * appendPeople (Append multiple properties)
   * Store user profiles in Mixpanel's People Analytics product
   * @param {Object} properties
  */
   appendMultipleProperties(properties: any = {}): void {
    this.run((mp) => mp.people.union(properties));
  }

  /**
   * Method to set user properties in Mixpanel if the property doesn't exist
   */
  setPeopleOnce(properties) {
    this.run((mp) => mp.people.set_once(properties));
  }
}